import React, { useEffect, useState } from 'react'
import { Alert, AlertTitle, Skeleton, Stack, Typography } from '@mui/material'
import API from '../../../services/API'

const INVOICE_GROUPING_LABELS = {
  perJourney: 'one invoice per journey',
  perBooking: 'one invoice per booking',
  perPatient: 'one invoice per patient',
  consolidatedWeekly: 'consolidated weekly',
  consolidatedMonthly: 'consolidated monthly',
}

// Shows how this booking will be billed before it's created — which tariff
// resolves, the organisation's invoice grouping, and whether a contract even
// exists yet — reusing the same resolution the booking page's own Billing
// Setup section shows after creation (GET /pts/organisations/:id/billing-setup
// calls the same resolveTariffForBooking helper).
const BillingPreview = ({ organisationId, patientIsCustomer }) => {
  const [billingSetup, setBillingSetup] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (patientIsCustomer || !organisationId) {
      setBillingSetup(null)
      return
    }
    setLoading(true)
    API.get(`/pts/organisations/${organisationId}/billing-setup`)
      .then((res) => {
        if (res.status === 200) setBillingSetup(res.data)
        else setBillingSetup(null)
      })
      .catch(() => setBillingSetup(null))
      .finally(() => setLoading(false))
  }, [organisationId, patientIsCustomer])

  if (patientIsCustomer) {
    return (
      <Alert severity="info" variant="outlined">
        <AlertTitle>Patient billing</AlertTitle>
        This booking will be billed directly to the patient. After creating the
        booking, use the booking's Billing area to generate quotes or create
        invoices.
      </Alert>
    )
  }

  if (!organisationId) {
    return (
      <Alert severity="info" variant="outlined">
        <AlertTitle>Quote and invoice setup</AlertTitle>
        Select the requesting organisation to preview which tariff and invoice
        grouping will be used. After creating the booking, the booking page will
        show the Generate Quote and Create Invoice actions.
      </Alert>
    )
  }
  if (loading) return <Skeleton variant="rounded" height={52} />
  if (!billingSetup) {
    return (
      <Alert severity="warning" variant="outlined">
        <AlertTitle>Unable to preview billing</AlertTitle>
        The booking can still be created, but tariff and invoice details could
        not be previewed right now.
      </Alert>
    )
  }

  const groupingLabel =
    INVOICE_GROUPING_LABELS[billingSetup.invoiceGrouping] ||
    billingSetup.invoiceGrouping

  return (
    <Alert severity={billingSetup.tariff ? 'info' : 'warning'} variant="outlined">
      <AlertTitle>Quote and invoice setup</AlertTitle>
      <Stack spacing={0.75}>
        <Typography variant="body2">
          {billingSetup.tariff ? (
            <>
              Quotes and auto-priced invoices will use{' '}
              <strong>{billingSetup.tariff.name}</strong>
              {billingSetup.tariff.isDefault ? ' (tenant default tariff)' : ''}.
              Invoices will be grouped as <strong>{groupingLabel}</strong>.
            </>
          ) : (
            "No tariff resolves for this organisation yet. The booking can be created, but quotes and auto-priced invoices won't be able to price it until a default tariff is set up in Pricing & Tariffs."
          )}
        </Typography>
        {billingSetup.tariff && !billingSetup.contractExists && (
          <Typography variant="body2">
            No contract is set up yet for this organisation, so the tenant
            default tariff and {groupingLabel} default will be used.
          </Typography>
        )}
        {billingSetup.requiresQuote && (
          <Typography variant="body2" fontWeight={700}>
            A quote is required before this booking can move into planning.
          </Typography>
        )}
        <Typography variant="body2">
          Once the booking is created, you will be taken to its Billing area to
          generate a quote or create an invoice.
        </Typography>
      </Stack>
    </Alert>
  )
}

export default BillingPreview
